import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Heart, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { apiRequest } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function HealthAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const r = await apiRequest("/health-alerts");
      setData(r);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiRequest("/health-alerts/refresh", { method: "POST" });
      toast.success("Health alerts ricalcolati", {
        description: `${r.alerts_count} alert attivi · WS notifiche inviate: ${r.ws_delivered}`,
      });
      await load();
    } catch (err) {
      toast.error("Refresh fallito", { description: err.message });
    } finally {
      setRefreshing(false);
    }
  };

  const dismiss = async (alert) => {
    if (!window.confirm(`Chiudere l'alert per ${alert.business_name}?`)) return;
    try {
      await apiRequest(`/health-alerts/${alert.id}/dismiss`, { method: "POST" });
      toast.success("Alert chiuso");
      await load();
    } catch (err) {
      toast.error("Dismiss fallito", { description: err.message });
    }
  };

  if (loading) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="health-alerts-loading">Caricamento health alerts...</div>;
  const alerts = data?.alerts || [];

  return (
    <div className="space-y-6" data-testid="health-alerts-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
          <Heart className="mr-1 inline h-3 w-3" /> Allerte salute portafoglio
        </p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="health-alerts-title">Health alerts</h2>
        <p className="mt-2 text-slate-600">
          Aziende con health_score sotto la soglia critica di <strong>50/100</strong>. Il health score combina completion DVR, accettazione AI, gestione scadenze, engagement newsletter e firme. Quando un'azienda scende sotto 50, ricevi una notifica push in tempo reale.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={refresh} disabled={refreshing} className="bg-blue-900 hover:bg-blue-800" data-testid="health-alerts-refresh-button">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Ricalcolo..." : "Ricalcola alert"}
          </Button>
          <Badge className="bg-rose-50 text-rose-700" data-testid="health-alerts-count-badge">
            {alerts.length} attivi
          </Badge>
        </div>
      </section>

      {error && <Alert variant="destructive" data-testid="health-alerts-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      {alerts.length === 0 ? (
        <Card className="shadow-none" data-testid="health-alerts-empty-card">
          <CardContent className="p-12 text-center">
            <Heart className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="mt-3 font-semibold text-slate-950" data-testid="health-alerts-empty">Nessun alert attivo</p>
            <p className="mt-1 text-sm text-slate-500">Tutte le aziende sono sopra la soglia 50. Buon lavoro!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="health-alerts-list">
          {alerts.map((a) => (
            <Card key={a.id} className="shadow-none border-rose-200" data-testid={`health-alert-${a.id}`}>
              <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Link to={`/aziende/${a.company_id}/dashboard`} className="font-heading text-lg font-semibold text-slate-950 hover:text-blue-900 hover:underline" data-testid={`health-alert-company-${a.id}`}>
                    {a.business_name}
                  </Link>
                  <p className="mt-1 text-xs text-slate-500" data-testid={`health-alert-meta-${a.id}`}>
                    Attivato il {(a.triggered_at || "").slice(0, 19).replace("T", " ")} · DVR {a.completion_percentage}% · {a.actions_overdue} azioni scadute · {a.high_risks_count} rischi alti
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-rose-100 text-rose-800 border-rose-200" data-testid={`health-alert-score-${a.id}`}>
                    Health: {a.health_score}/100
                  </Badge>
                  <Button asChild variant="outline" size="sm" data-testid={`health-alert-open-${a.id}`}>
                    <Link to={`/aziende/${a.company_id}/dashboard`}>Apri dashboard</Link>
                  </Button>
                  <Button variant="outline" size="sm" className="text-rose-700" onClick={() => dismiss(a)} data-testid={`health-alert-dismiss-${a.id}`}>
                    <X className="h-3.5 w-3.5" /> Chiudi
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
